import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  MoveDown,
  MoveUp,
  SwapHoriz,
  LocalShipping,
  CompareArrows,
  ViewList,
  Add,
  ArrowBack,
  Refresh,
  Download
} from '@mui/icons-material';
import OperationsList from '../../components/OperationsList';
import { Operation, TransferType } from '../../types/Operation.types';
import { useNavigate } from 'react-router-dom';
import { DataService } from '../../services/data-service';
import { useAppDispatch } from '../../redux/hooks';
import { showAlert } from '../../redux/alert/alertReducer';
import { format } from 'date-fns';

interface OperationsListPageProps {
  initialTransferType?: TransferType | null;
  title?: string;
  showTypeFilter?: boolean;
  showBackButton?: boolean;
  backPath?: string;
  showCreateButton?: boolean;
  createPath?: string;
  showRefreshButton?: boolean;
  showExportButton?: boolean;
  exportEndpoint?: string;
}

const getTransferTypeInfo = (type: TransferType | null) => {
  if (type === null) {
    return { label: 'All Operations', color: 'default' as const, icon: <ViewList /> };
  }

  switch (type) {
    case TransferType.MoveIn:
      return { label: 'Move In', color: 'success' as const, icon: <MoveDown /> };
    case TransferType.MoveOut:
      return { label: 'Move Out', color: 'warning' as const, icon: <MoveUp /> };
    case TransferType.Reallocation:
      return { label: 'Reallocation', color: 'info' as const, icon: <SwapHoriz /> };
    case TransferType.Shipment:
      return { label: 'Shipment', color: 'primary' as const, icon: <LocalShipping /> };
    case TransferType.Transfer:
      return { label: 'Transfer', color: 'secondary' as const, icon: <CompareArrows /> };
    default:
      return { label: 'All Operations', color: 'default' as const, icon: <ViewList /> };
  }
};

const OperationsListPage: React.FC<OperationsListPageProps> = ({
  initialTransferType = null,
  title,
  showTypeFilter = true,
  showBackButton = false,
  backPath = '/movements',
  showCreateButton = false,
  createPath,
  showRefreshButton = true,
  showExportButton = true,
  exportEndpoint
}) => {
  const [selectedTransferType, setSelectedTransferType] = useState<TransferType | null>(initialTransferType);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const operationsListRef = useRef<{ refresh: () => void }>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleOperationSelect = (operation: Operation) => {
    navigate(`/movements/operation/${operation.id}`);
  };

  const handleTransferTypeChange = (type: TransferType | null) => {
    setSelectedTransferType(type);
  };

  const handleRefresh = async () => {
    if (operationsListRef.current) {
      setRefreshing(true);
      operationsListRef.current.refresh();
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  const handleExport = async () => {
    if (!exportEndpoint) return;

    try {
      const response = await DataService.get(exportEndpoint);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `operations-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        dispatch(showAlert({ message: 'Export completed successfully', type: 'success' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Export failed', type: 'error' }));
    }
  };

  const handleTotalCountUpdate = (count: number) => {
    setTotalCount(count);
  };

  // Sync selectedTransferType with initialTransferType when route changes
  useEffect(() => {
    setSelectedTransferType(initialTransferType);
  }, [initialTransferType]);

  const transferTypeInfo = getTransferTypeInfo(selectedTransferType);

  const transferTypeOptions = [
    { value: null, ...getTransferTypeInfo(null) },
    { value: TransferType.MoveIn, ...getTransferTypeInfo(TransferType.MoveIn) },
    { value: TransferType.MoveOut, ...getTransferTypeInfo(TransferType.MoveOut) },
    { value: TransferType.Reallocation, ...getTransferTypeInfo(TransferType.Reallocation) },
    { value: TransferType.Shipment, ...getTransferTypeInfo(TransferType.Shipment) },
    { value: TransferType.Transfer, ...getTransferTypeInfo(TransferType.Transfer) }
  ];

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider', p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            {showBackButton && (
              <IconButton onClick={() => navigate(backPath)}>
                <ArrowBack />
              </IconButton>
            )}
            {transferTypeInfo.icon}
            <Box>
              <Typography variant="h5" component="h5" sx={{ fontWeight: 'bold' }} data-testid="ops-header">
                {title || `${transferTypeInfo.label} Operations`}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="ops-subtitle">
                View and manage {transferTypeInfo.label.toLowerCase()} operations
              </Typography>
            </Box>
            <Chip
              label={`${totalCount} Total`}
              size="small"
              color={transferTypeInfo.color}
              variant="outlined"
              data-testid="ops-total"
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            {showRefreshButton && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                startIcon={<CircularProgress size={16} sx={{ display: refreshing ? 'inline-flex' : 'none' }} />}
                disabled={refreshing}
                aria-label="Refresh"
              >
                <Refresh sx={{ display: refreshing ? 'none' : 'inline-flex' }} />
                Refresh
              </Button>
            )}
            {showExportButton && exportEndpoint && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleExport}
                startIcon={<Download />}
                aria-label="Export"
              >
                Export
              </Button>
            )}
            {showCreateButton && createPath && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate(createPath)}
              >
                Create New
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {showTypeFilter && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="medium" sx={{ maxWidth: 400 }}>
              <InputLabel>Filter by Operation Type</InputLabel>
              <Select
                value={selectedTransferType || ''}
                onChange={(e) => handleTransferTypeChange(e.target.value === '' ? null : e.target.value as TransferType)}
                label="Filter by Operation Type"
              >
                {transferTypeOptions.map((option, index) => (
                  <MenuItem key={index} value={option.value || ''}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {option.icon}
                      <Typography>{option.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <OperationsList
          ref={operationsListRef}
          transferType={selectedTransferType}
          onOperationSelect={handleOperationSelect}
          onTotalCountUpdate={handleTotalCountUpdate}
          hideHeader={true}
        />
      </Box>
    </Box>
  );
};

export default OperationsListPage;